import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_URL } from '../../common/Constants';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { formatUsdFromCents } from '../../utils/formatCurrency';

const planNames = { S: 'Starter', P: 'Pro', U: 'Ultimate' };

/**
 * Blocking payment prompt. User must pay (Stripe) or skip (free plan).
 * onNoPendingPlan: called when API reports no pending plan (e.g. already cleared).
 */
export default function CompletePaymentDialog({ open, onNoPendingPlan }) {
  const onNoPendingPlanRef = useRef(onNoPendingPlan);
  onNoPendingPlanRef.current = onNoPendingPlan;

  const [loading, setLoading] = useState(true);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [planPrice, setPlanPrice] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setLoading(true);
      setPendingPlan(null);
      setError('');
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const [pendingRes, plansRes] = await Promise.all([
          axios.get(`${API_URL}/account/pending-plan`),
          axios.get(`${API_URL}/account/plans`)
        ]);

        if (!pendingRes.data.success || !pendingRes.data.pendingPlan) {
          onNoPendingPlanRef.current?.();
          return;
        }

        const plan = pendingRes.data.pendingPlan;
        const cycle = pendingRes.data.billingCycle || 'monthly';
        setPendingPlan(plan);
        setBillingCycle(cycle);

        if (plansRes.data.success && Array.isArray(plansRes.data.plans)) {
          const match = plansRes.data.plans.find((p) => p.value === plan);
          if (match) {
            const cents = cycle === 'annual' ? match.annualPriceCents : match.monthlyPriceCents;
            setPlanPrice(formatUsdFromCents(cents));
          }
        }
      } catch (err) {
        console.error('CompletePaymentDialog: fetch failed', err);
        setError('Unable to load payment information. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [open]);

  const handlePayNow = async () => {
    setRedirecting(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/payment/create-checkout-session`, {
        plan: pendingPlan,
        billing_cycle: billingCycle
      });
      if (res.data.success && res.data.url) {
        window.location.href = res.data.url;
        return;
      }
      setError(res.data.message || 'Failed to create checkout session');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start payment. Please try again.');
    } finally {
      setRedirecting(false);
    }
  };

  const handleSkip = async () => {
    setSkipping(true);
    setError('');
    try {
      await axios.post(`${API_URL}/account/clear-pending-plan`);
      window.location.href = '/#/Dashboard';
    } catch (err) {
      setError('Failed to skip. Please try again.');
      setSkipping(false);
    }
  };

  const handleDialogClose = (_event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
  };

  const planName = pendingPlan ? (planNames[pendingPlan] || pendingPlan) : '';

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      scroll="paper"
      slotProps={{
        backdrop: {
          sx: { pointerEvents: 'auto' }
        }
      }}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 2
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
        <PaymentIcon sx={{ fontSize: 48, color: 'primary.main', display: 'block', mx: 'auto', mb: 1 }} />
        <Typography component="span" variant="h5" sx={{ fontWeight: 700 }}>
          Complete Your Payment
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pb: 4, px: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && pendingPlan && (
          <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'transparent' }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              You selected the <strong>{planName}</strong> plan
              {planPrice && <> at <strong>{planPrice}/mo</strong></>}
              {billingCycle === 'annual' && <> (annual billing)</>}
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Complete your payment to activate your {planName} plan features.
              You can also skip and use the free trial plan instead.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handlePayNow}
                disabled={redirecting || skipping}
                startIcon={redirecting ? <CircularProgress size={20} color="inherit" /> : <PaymentIcon />}
                sx={{ fontWeight: 700, fontSize: 16, py: 1.5, px: 5, minWidth: 250 }}
              >
                {redirecting ? 'Redirecting to Stripe...' : `Pay & Activate ${planName}`}
              </Button>

              <Button
                variant="text"
                color="inherit"
                size="small"
                onClick={handleSkip}
                disabled={redirecting || skipping}
                startIcon={skipping ? <CircularProgress size={16} color="inherit" /> : <SkipNextIcon />}
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': { color: 'text.primary' }
                }}
              >
                {skipping ? 'Skipping...' : 'Skip — Use Free Plan'}
              </Button>
            </Box>
          </Paper>
        )}
        {!loading && !pendingPlan && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
}
